import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { InviteFarmerDialog } from "features/programmeInvitations/InviteFarmerDialog";
import { PendingInvitationsList } from "features/programmeInvitations/PendingInvitationsList";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { useManagedProgrammes } from "model/useProgrammeInvitationQueries";
import { useState } from "react";
import InsightsSection from "./InsightsSection";
import { ActionFarmersTable } from "./corporate/ActionFarmersTable";
import { FarmerTable } from "./corporate/FarmerTable";

type FarmerView = "byFarmer" | "byAction";

export function AccessibleFarmsInsights() {
  const { data: accessibleFarmers, isLoading } =
    useAccessibleFarmerProgrammes();
  const { data: managedProgrammes } = useManagedProgrammes();

  const hasAccess = (accessibleFarmers?.length ?? 0) > 0;
  const canInvite = (managedProgrammes?.length ?? 0) > 0;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [view, setView] = useState<FarmerView>("byFarmer");

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        minHeight: "100%",
        paddingTop: "20px",
        paddingBottom: "20px",
        overflowX: "hidden",
      }}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : hasAccess ? (
        <>
          <InsightsSection title="Progress">
            <Tabs
              value={view}
              onChange={(_, next: FarmerView) => setView(next)}
              sx={{ mb: 2, minHeight: 36 }}
            >
              <Tab
                label="By farmer"
                value="byFarmer"
                sx={{ minHeight: 36, textTransform: "none" }}
              />
              <Tab
                label="By action"
                value="byAction"
                sx={{ minHeight: 36, textTransform: "none" }}
              />
            </Tabs>
            {view === "byFarmer" ? <FarmerTable /> : <ActionFarmersTable />}
          </InsightsSection>
          {canInvite && view === "byFarmer" && (
            <Box sx={{ px: 3, mt: 3 }}>
              <PendingInvitationsList
                onInviteClick={() => setInviteOpen(true)}
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            You don't have access to individual farmer data for your programme.
          </Typography>
          {canInvite && (
            <Button variant="contained" onClick={() => setInviteOpen(true)}>
              Invite your first farmer
            </Button>
          )}
        </Box>
      )}
      <InviteFarmerDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </Box>
  );
}
